import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Select, MenuItem,
    Switch, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, CircularProgress,
    useMediaQuery, useTheme
} from '@mui/material';
import {
    Close as CloseIcon,
    CalendarToday as CalendarIcon,
    KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from "../../services/api";

const dialogPaperSx = {
    background: 'linear-gradient(135deg, #1a1510 0%, #0f0d0a 100%)',
    border: '1px solid rgba(188, 150, 94, 0.93)',
    borderRadius: 1,
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8)',
};

// Fieldset-style wrapper — label sits above the box, matches AddCouponDialog
const FieldGroup = ({ label, required, error, helperText, disabled, children, sx }) => (
    <Box sx={{ ...sx }}>
        <Box
            component="fieldset"
            sx={{
                border: '1px solid',
                borderColor: error ? '#f44336' : 'rgba(188, 150, 94, 0.86)',
                borderRadius: 1,
                m: 0,
                px: 1.5,
                pt: 0,
                pb: 0.25,
                minWidth: 0,
                background: disabled ? 'rgba(255,255,255,0.015)' : 'rgba(255,255,255,0.03)',
                opacity: disabled ? 0.7 : 1,
                transition: 'border-color 0.15s ease',
                '&:hover': { borderColor: error ? '#f44336' : disabled ? 'rgba(188,150,94,0.3)' : 'rgba(188,150,94,0.5)' },
                '&:focus-within': { borderColor: error ? '#f44336' : '#bc965e' },
            }}
        >
            <Box
                component="legend"
                sx={{
                    px: 0.75,
                    fontSize: 12,
                    lineHeight: 1,
                    color: error ? '#f44336' : 'rgba(255,255,255,0.6)',
                    letterSpacing: 0.3,
                }}
            >
                {label}{required && ' *'}
            </Box>
            {children}
        </Box>
        {helperText && (
            <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, ml: 1.5, color: error ? '#f44336' : 'rgba(255,255,255,0.4)' }}
            >
                {helperText}
            </Typography>
        )}
    </Box>
);

const bareInputSx = {
    '& .MuiInputBase-root': { '&:before, &:after': { display: 'none' } },
    '& .MuiInputBase-input': {
        color: 'white',
        fontSize: '0.95rem',
        py: 0.25,
        '&::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
        '&.Mui-disabled': { WebkitTextFillColor: 'rgba(255,255,255,0.6)' },
    },
};

const bareSelectSx = {
    color: 'white',
    fontSize: '0.95rem',
    '&:before, &:after': { display: 'none' },
    '& .MuiSelect-select': { py: 0.25 },
    '& .MuiSvgIcon-root': { color: '#bc965e' },
};

const EMPTY_FORM = {
    CouponCode: '',
    DiscountType: 'percentage',
    DiscountValue: '',
    MinOrderAmount: '',
    MaxDiscountAmount: '',
    IsActive: true,
    ExpiryDate: '',
    ApplicableTo: 'all',
    ProductCode: '',
};

const EditCouponDialog = ({ open, coupon, onClose, onUpdated }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (coupon) {
            setForm({
                CouponCode: coupon.CouponCode,
                DiscountType: coupon.DiscountType,
                DiscountValue: coupon.DiscountValue,
                MinOrderAmount: coupon.MinOrderAmount || '',
                MaxDiscountAmount: coupon.MaxDiscountAmount || '',
                IsActive: coupon.IsActive,
                ExpiryDate: coupon.ExpiryDate ? coupon.ExpiryDate.split('T')[0] : '',
                ApplicableTo: coupon.ApplicableTo || 'all',
                ProductCode: coupon.ProductCode || '',
            });
            setErrors({});
        }
    }, [coupon]);

    const updateField = (field, value) => {
        setForm(p => ({ ...p, [field]: value }));
        if (errors[field]) setErrors(p => ({ ...p, [field]: undefined }));
    };

    const validate = () => {
        const next = {};

        if (!form.CouponCode.trim()) {
            next.CouponCode = 'Coupon code is required';
        }

        if (!form.DiscountValue || isNaN(form.DiscountValue) || Number(form.DiscountValue) <= 0) {
            next.DiscountValue = 'Enter a valid discount value';
        } else if (form.DiscountType === 'percentage' && Number(form.DiscountValue) > 100) {
            next.DiscountValue = 'Percentage discount cannot exceed 100%';
        }

        if (form.ApplicableTo === 'product' && !form.ProductCode.trim()) {
            next.ProductCode = 'Product code is required';
        }

        if (!form.ExpiryDate) {
            next.ExpiryDate = 'Expiry date is required';
        } else {
            const today = new Date().toISOString().split('T')[0];
            if (form.ExpiryDate < today) {
                next.ExpiryDate = 'Expiry date cannot be in the past';
            }
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setSaving(true);
        try {
            const payload = {
                CouponCode: form.CouponCode.toUpperCase().trim(),
                DiscountType: form.DiscountType,
                DiscountValue: Number(form.DiscountValue),
                MinOrderAmount: form.MinOrderAmount ? Number(form.MinOrderAmount) : 0,
                MaxDiscountAmount: form.MaxDiscountAmount ? Number(form.MaxDiscountAmount) : null,
                IsActive: form.IsActive,
                ExpiryDate: form.ExpiryDate || null,
                ApplicableTo: form.ApplicableTo,
                ProductCode: form.ApplicableTo === 'product' ? form.ProductCode : null,
            };

            const res = await api.put(`/coupons/${coupon.CouponId}`, payload);

            if (res.data.success) {
                toast.success('Coupon updated!');
                onClose();
                onUpdated();
            } else {
                toast.error(res.data.message || 'Failed to update coupon');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update coupon');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !saving && onClose()}
            maxWidth="sm"
            fullWidth
          
            PaperProps={{ sx: dialogPaperSx }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Edit Coupon</Typography>
                <IconButton onClick={onClose} disabled={saving} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>

                    <FieldGroup label="Coupon Code" required error={!!errors.CouponCode}
                        helperText={errors.CouponCode || 'Customers will enter this at checkout'}>
                        <TextField
                            value={form.CouponCode}
                            onChange={(e) => updateField('CouponCode', e.target.value.toUpperCase())}
                            placeholder="e.g. SAVE20"
                            fullWidth
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 } }}
                            sx={bareInputSx}
                        />
                    </FieldGroup>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <FieldGroup label="Type" required sx={{ flex: 1, minWidth: 130 }}>
                            <Select
                                value={form.DiscountType}
                                onChange={(e) => updateField('DiscountType', e.target.value)}
                                variant="standard"
                                disableUnderline
                                IconComponent={ArrowDownIcon}
                                fullWidth
                                sx={bareSelectSx}
                                MenuProps={{ PaperProps: { sx: { background: '#1a1510', border: '1px solid rgba(188,150,94,0.3)' } } }}
                            >
                                <MenuItem value="percentage" sx={{ color: 'white' }}>Percentage (%)</MenuItem>
                                <MenuItem value="fixed" sx={{ color: 'white' }}>Fixed Amount (₹)</MenuItem>
                            </Select>
                        </FieldGroup>

                        <FieldGroup
                            label={form.DiscountType === 'fixed' ? 'Discount (₹)' : 'Discount (%)'}
                            required
                            error={!!errors.DiscountValue}
                            helperText={errors.DiscountValue}
                            sx={{ flex: 1, minWidth: 120 }}
                        >
                            <TextField
                                value={form.DiscountValue}
                                onChange={(e) => updateField('DiscountValue', e.target.value)}
                                type="number"
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                inputProps={{ min: 0, max: form.DiscountType === 'percentage' ? 100 : undefined }}
                                fullWidth
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                    </Box>

                    <FieldGroup label="Applicable To" required>
                        <Select
                            value={form.ApplicableTo}
                            onChange={(e) => {
                                setForm(p => ({ ...p, ApplicableTo: e.target.value, ProductCode: '' }));
                                if (errors.ProductCode) setErrors(p => ({ ...p, ProductCode: undefined }));
                            }}
                            variant="standard"
                            disableUnderline
                            IconComponent={ArrowDownIcon}
                            fullWidth
                            sx={bareSelectSx}
                            MenuProps={{ PaperProps: { sx: { background: '#1a1510', border: '1px solid rgba(188,150,94,0.3)' } } }}
                        >
                            <MenuItem value="all" sx={{ color: 'white' }}>All Products (Entire Order)</MenuItem>
                            <MenuItem value="product" sx={{ color: 'white' }}>Specific Product Only</MenuItem>
                        </Select>
                    </FieldGroup>

                    {form.ApplicableTo === 'product' && (
                        <FieldGroup label="Product Code" required error={!!errors.ProductCode}
                            helperText={errors.ProductCode || 'Coupon will only apply to this specific product in cart'}>
                            <TextField
                                value={form.ProductCode}
                                onChange={(e) => updateField('ProductCode', e.target.value.toUpperCase())}
                                placeholder="e.g. PROD001"
                                fullWidth
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <FieldGroup label="Min Order Amount (₹)" helperText="Leave 0 for no minimum" sx={{ flex: '1 1 140px' }}>
                            <TextField
                                value={form.MinOrderAmount}
                                onChange={(e) => updateField('MinOrderAmount', e.target.value)}
                                type="number"
                                placeholder="0"
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                fullWidth
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                        <FieldGroup label="Max Discount (₹)" helperText="Cap for % discounts" sx={{ flex: '1 1 140px' }}>
                            <TextField
                                value={form.MaxDiscountAmount}
                                onChange={(e) => updateField('MaxDiscountAmount', e.target.value)}
                                type="number"
                                placeholder="Optional"
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                fullWidth
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                    </Box>

                    <FieldGroup label="Expiry Date" required error={!!errors.ExpiryDate}
                        helperText={errors.ExpiryDate || 'Click anywhere on the field to pick a date'}>
                        <TextField
                            value={form.ExpiryDate}
                            onChange={(e) => updateField('ExpiryDate', e.target.value)}
                            type="date"
                            fullWidth
                            variant="standard"
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                            InputProps={{
                                disableUnderline: true,
                                endAdornment: <CalendarIcon sx={{ color: '#bc965e', fontSize: 18, pointerEvents: 'none' }} />,
                            }}
                            sx={{
                                ...bareInputSx,
                                position: 'relative',
                                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    opacity: 0,
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    cursor: 'pointer',
                                    zIndex: 1,
                                },
                            }}
                        />
                    </FieldGroup>

                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 2, borderRadius: 2, border: '1px solid rgba(188,150,94,0.2)',
                        background: 'rgba(255,255,255,0.02)',
                    }}>
                        <Box>
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>Active</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Inactive coupons cannot be used at checkout
                            </Typography>
                        </Box>
                        <Switch
                            checked={form.IsActive}
                            onChange={(e) => updateField('IsActive', e.target.checked)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#bc965e' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#bc965e' },
                            }}
                        />
                    </Box>

                    {Object.keys(errors).length > 0 && (
                        <Box sx={{
                            p: 1.5, borderRadius: 2,
                            background: 'rgba(244,67,54,0.08)',
                            border: '1px solid rgba(244,67,54,0.3)',
                        }}>
                            <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 700, display: 'block', mb: 0.5 }}>
                                Please fix the following:
                            </Typography>
                            {Object.values(errors).filter(Boolean).map((msg, i) => (
                                <Typography key={i} variant="caption" sx={{ color: '#f44336', display: 'block' }}>
                                    • {msg}
                                </Typography>
                            ))}
                        </Box>
                    )}

                   

                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                <Button onClick={onClose} disabled={saving} fullWidth={isMobile}
                    sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth={isMobile}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                        background: 'linear-gradient(45deg, #bc965e, #d4af37)',
                        fontWeight: 600,
                        '&:hover': { background: 'linear-gradient(45deg, #a8834d, #c19b2a)' },
                        '&:disabled': { background: 'rgba(188,150,94,0.3)', color: 'rgba(255,255,255,0.5)' },
                    }}
                >
                    {saving ? 'Saving...' : 'Update Coupon'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCouponDialog;